import { buildGroupedSlotModel, inferActiveGroupName } from './buildGroupedSlotModel';

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Returns slot IDs to validate for a step (active group only when GROUPED).
 *
 * @param {Object} options
 * @param {Object} options.step
 * @param {Map} options.personalisationSlots
 * @param {Array} options.selections
 * @returns {string[]}
 */
export function getStepSlotIdsForValidation({ step, personalisationSlots, selections }) {
  const stepSlots = step?.slots;
  if (!stepSlots || !Array.isArray(stepSlots)) {
    return [];
  }

  if (step.slotMode !== 'GROUPED') {
    return stepSlots;
  }

  const stepSlotObjects = stepSlots.map((slotId) => personalisationSlots.get(slotId)).filter(Boolean);

  const { optionGroups } = buildGroupedSlotModel(stepSlotObjects);
  const activeGroupName = inferActiveGroupName({ optionGroups, selections });
  const activeGroup = optionGroups.find((group) => group.groupName === activeGroupName);

  return activeGroup?.slots.map((slot) => slot.id) ?? [];
}

/**
 * Optional UV inserts only count as configured once the front face has at least one
 * piece of customer content, matching the legacy gift-wrap journey behaviour.
 * @param {Object} options
 * @param {Object|null|undefined} options.selectedValue
 * @param {Object|null|undefined} options.resolvedUvInserts
 * @returns {boolean}
 */
export function hasValidUvInsertFrontContent({ selectedValue, resolvedUvInserts }) {
  if (!selectedValue || typeof selectedValue !== 'object' || Array.isArray(selectedValue)) {
    return false;
  }

  const configHandle = selectedValue.configHandle;
  if (!configHandle || !resolvedUvInserts?.configs) {
    return true;
  }

  const activeConfig = resolvedUvInserts.configs.find((config) => config.handle === configHandle);
  if (!activeConfig) {
    return true;
  }

  const frontFaces = activeConfig.faceElements.filter((face) => face.location === 'FRONT');
  if (frontFaces.length === 0) {
    return true;
  }

  let hasConfigurableFrontField = false;
  let hasFrontText = false;
  let hasFrontImage = false;

  frontFaces.forEach((face) => {
    face.elementConfig.forEach((element) => {
      const propertyKey = typeof element.lineItemPropertyKey === 'string' ? element.lineItemPropertyKey.trim() : '';
      if (!propertyKey) return;

      if (element.type === 'TEXT') {
        hasConfigurableFrontField = true;
        if (isNonEmptyString(selectedValue.textByKey?.[propertyKey])) {
          hasFrontText = true;
        }
      }

      if (element.type === 'IMAGE_UPLOAD') {
        hasConfigurableFrontField = true;
        if (selectedValue.imageByKey?.[propertyKey]) {
          hasFrontImage = true;
        }
      }
    });
  });

  if (!hasConfigurableFrontField) {
    return true;
  }

  return hasFrontText || hasFrontImage;
}

/**
 * Returns whether all optional UV insert selections on the step are complete enough for progression.
 * Optional UV inserts only become incomplete when they are opted in but their front face is still blank.
 * @param {Object} options
 * @param {Object} options.step
 * @param {Map} options.personalisationSlots
 * @param {Array} options.selections
 * @param {Object|null|undefined} options.resolvedUvInserts
 * @returns {boolean}
 */
export function isOptionalUvInsertComplete({ step, personalisationSlots, selections, resolvedUvInserts }) {
  if (!step?.slots || !Array.isArray(step.slots)) {
    return true;
  }

  return step.slots.every((slotId) => {
    const slot = personalisationSlots?.get(slotId);
    if (!slot || slot.slotType !== 'UV_INSERT') return true;

    const isSlotRequired = slot.isRequired === true || slot.isRequired === 'true';
    if (isSlotRequired) return true;

    const selection = selections.find((selectionItem) => selectionItem.id === slotId);
    if (!selection?.selected) return true;

    return hasValidUvInsertFrontContent({
      selectedValue: selection.selected,
      resolvedUvInserts
    });
  });
}

/**
 * Validates if a step is complete based on its slots and requirements.
 * @param {Object} options - Validation options.
 * @param {Object} options.step - The step object containing slots and minRequired.
 * @param {Array} options.step.slots - Array of slot objects for the step.
 * @param {number} options.step.minRequired - Minimum number of selections required for the step.
 * @param {Map} options.personalisationSlots - Map of all personalisation slots.
 * @param {Array} options.selections - Array of current selections.
 * @returns {Object} Validation result with isValid boolean and validation details.
 */
export function validateStep({ step, personalisationSlots, selections }) {
  if (!step || !step.slots || !Array.isArray(step.slots)) {
    return {
      isValid: false,
      validationDetails: {
        requiredSlots: [],
        minRequiredMet: false,
        totalSelections: 0,
        requiredSelections: 0
      }
    };
  }

  const { minRequired = 0 } = step;
  const slotIdsToValidate = getStepSlotIdsForValidation({ step, personalisationSlots, selections });
  const minRequiredNumber = Number(minRequired);
  let totalSelections = 0;
  let requiredSelections = 0;
  const requiredSlots = [];

  // Check each slot in the step (active group only for GROUPED mode).
  slotIdsToValidate.forEach((slotId) => {
    const slot = personalisationSlots.get(slotId);
    if (!slot) return;

    const selection = selections.find((selectionItem) => selectionItem.id === slotId);

    // Validate the slot selection
    const isValidSelection = validateSlotSelection({ slot, selectedValue: selection?.selected });
    const hasSelection = isValidSelection;

    // Convert string 'true'/'false' to boolean if needed.
    const isSlotRequired = slot.isRequired === true || slot.isRequired === 'true';

    // Check if slot is required and has a selection.
    if (isSlotRequired) {
      requiredSlots.push({
        slotId,
        slotType: slot.slotType,
        isSelected: hasSelection,
        isValid: isValidSelection,
        slot
      });

      if (hasSelection) {
        requiredSelections++;
      }
    } else {
      // For non-required slots, still track validation for styling.
      requiredSlots.push({
        slotId,
        slotType: slot.slotType,
        isSelected: true, // Non-required slots are always considered "selected" for progression.
        isValid: isValidSelection,
        slot
      });
    }

    // Count all selections for minRequired validation.
    if (hasSelection) {
      totalSelections++;
    }
  });

  // Check if all required slots are filled.
  const allRequiredFilled = requiredSlots.every((slot) => slot.isSelected);

  // Check if minimum required selections are met.
  // minRequired counts ALL selections (including required ones).
  const minRequiredMet = totalSelections >= minRequiredNumber;

  // Step is valid if all required slots are filled AND minimum required is met.
  const isValid = allRequiredFilled && minRequiredMet;

  return {
    isValid,
    validationDetails: {
      requiredSlots,
      minRequiredMet,
      totalSelections,
      requiredSelections,
      minRequired: minRequiredNumber,
      // Add validation info for styling purposes.
      slotValidations: requiredSlots.map((slot) => ({
        slotId: slot.slotId,
        isValid: slot.isValid,
        isRequired: slot.slot.isRequired === true || slot.slot.isRequired === 'true'
      }))
    }
  };
}

/**
 * Determines if a step is required based on its slots and minRequired value.
 * @param {Object} options - Options for determining if step is required.
 * @param {Object} options.step - The step object containing slots and minRequired.
 * @param {Array} options.step.slots - Array of slot objects for the step.
 * @param {number} options.step.minRequired - Minimum number of selections required for the step.
 * @param {Map} options.personalisationSlots - Map of all personalisation slots.
 * @returns {boolean} Whether the step is required.
 */
export function isStepRequired({ step, personalisationSlots }) {
  if (!step || !step.slots || !Array.isArray(step.slots)) {
    return false;
  }

  const { slots: stepSlots, minRequired = 0 } = step;
  const minRequiredNumber = Number(minRequired);

  // Check if any slot is required.
  const hasRequiredSlots = stepSlots.some((slotId) => {
    const slot = personalisationSlots?.get(slotId);
    return slot?.isRequired === true || slot?.isRequired === 'true';
  });

  // Check if minRequired is greater than 0.
  const hasMinRequired = minRequiredNumber > 0;

  // Step is required if it has required slots OR minRequired > 0.
  return hasRequiredSlots || hasMinRequired;
}

/**
 * Validates if a specific slot selection is valid based on its type and requirements.
 * @param {Object} options - Validation options.
 * @param {Object} options.slot - The slot object.
 * @param {*} options.selectedValue - The selected value for the slot.
 * @param {Object|null|undefined} [options.resolvedUvInserts]
 * @returns {boolean} Whether the slot selection is valid.
 */
export function validateSlotSelection({ slot, selectedValue, resolvedUvInserts }) {
  if (!slot) return false;

  // For all slots, check based on slot type for validation styling.
  switch (slot.slotType) {
    case 'COMPONENT':
    case 'BASE_CONFIG':
      // Component slots require a non-empty selection.
      return Boolean(selectedValue && selectedValue.trim() !== '');

    case 'ALPHANUMERIC':
      // Alphanumeric slots require at least 1 character for valid styling.
      // Handle both string values and object values with text property.
      if (typeof selectedValue === 'string') {
        return Boolean(selectedValue && selectedValue.trim().length > 0);
      } else if (selectedValue && typeof selectedValue === 'object' && selectedValue.text) {
        return Boolean(selectedValue.text && selectedValue.text.trim().length > 0);
      }
      return false;

    case 'IMAGE_UPLOAD':
      // Image upload slots require a file to be selected.
      return Boolean(selectedValue && selectedValue !== '');

    case 'CHECKBOX':
      // Checkbox slots require the checkbox to be checked.
      return Boolean(selectedValue === true || selectedValue === 'true');

    case 'STAR_MAP':
      // Star map requires both locationPlace and datetime.
      if (selectedValue && typeof selectedValue === 'object') {
        return Boolean(selectedValue['locationPlace'] && selectedValue['datetime']);
      }
      // Also check if it's a string (legacy format).
      if (typeof selectedValue === 'string') {
        try {
          const parsed = JSON.parse(selectedValue);
          return Boolean(parsed && parsed['locationPlace'] && parsed['datetime']);
        } catch {
          return false;
        }
      }
      return false;

    case 'LOCATION_MAP':
      // Location map slots require locationPlace to be selected.
      if (selectedValue && typeof selectedValue === 'object') {
        return Boolean(selectedValue['locationPlace']);
      }
      // Also check if it's a string (legacy format)
      if (typeof selectedValue === 'string') {
        try {
          const parsed = JSON.parse(selectedValue);
          return Boolean(parsed && parsed['locationPlace']);
        } catch {
          return false;
        }
      }
      return false;

    case 'UV_INSERT':
      // When config data is available, only treat opted-in UV inserts as filled once the
      // front face contains customer content. Fall back to the legacy object-presence
      // check where the caller does not have resolved UV insert config.
      if (resolvedUvInserts) {
        return hasValidUvInsertFrontContent({ selectedValue, resolvedUvInserts });
      }
      return selectedValue !== null && selectedValue !== undefined;

    default:
      return false;
  }
}

/**
 * Gets validation messages for a step to display to the user.
 * @param {Object} validationResult - The result from validateStep function.
 * @returns {Array} Array of validation messages.
 */
export function getValidationMessages({ validationResult }) {
  const messages = [];
  const { validationDetails } = validationResult;

  // Check required slots.
  const unselectedRequiredSlots = validationDetails.requiredSlots.filter((slot) => !slot.isSelected);

  if (unselectedRequiredSlots.length > 0) {
    unselectedRequiredSlots.forEach((slot) => {
      const slotName = slot.slot.uiHeading || 'Selection';
      messages.push(`${slotName} is required.`);
    });
  }

  // Check minimum required.
  if (!validationDetails.minRequiredMet && validationDetails.minRequired > 0) {
    messages.push(`Please select at least ${validationDetails.minRequired} item(s).`);
  }

  return messages;
}

/**
 * Checks if a specific slot has valid content for styling purposes.
 * @param {Object} options - Validation options.
 * @param {Object} options.slot - The slot object.
 * @param {string} options.selectedValue - The selected value for the slot.
 * @returns {boolean} Whether the slot has valid content for styling.
 */
export function hasValidContent({ slot, selectedValue }) {
  return validateSlotSelection({ slot, selectedValue });
}
